import React, { useCallback, useEffect, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import UtilService from "../../../services/UtilService";
import AdminJigsawService from "../../jigsaw/AdminJigsawService";

function sourceLabel(source) {
    return source === "stripe" ? "Web (Stripe)" : "App";
}

export default function JigsawPackGrantsAdmin({ cloudUserId }) {
    const [grants, setGrants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const subjectUserId = typeof cloudUserId === "string" ? cloudUserId.trim() : "";

    const loadGrants = useCallback(async () => {
        if (!subjectUserId) {
            setGrants([]);
            return;
        }
        setLoading(true);
        setError("");
        const result = await AdminJigsawService.listUserPackGrants(subjectUserId);
        setLoading(false);
        if (!result.success) {
            setGrants([]);
            setError(result.error || "Failed to load pack purchases");
            return;
        }
        setGrants(Array.isArray(result.data?.grants) ? result.data.grants : []);
    }, [subjectUserId]);

    useEffect(() => {
        loadGrants();
    }, [loadGrants]);

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Pack purchases
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={loadGrants}
                        disabled={loading || !subjectUserId}
                        sx={{ minHeight: 44 }}
                    >
                        Refresh
                    </Button>
                </Box>
                {!subjectUserId ? (
                    <Alert severity="warning">User id is missing.</Alert>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : grants.length === 0 ? (
                    <Alert severity="info">No pack purchases for this user.</Alert>
                ) : (
                    <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Granted</TableCell>
                                    <TableCell>Pack</TableCell>
                                    <TableCell>Tier</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Transaction</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {grants.map((grant) => (
                                    <TableRow key={grant.id || grant.transactionId} hover>
                                        <TableCell>{UtilService.formatDate(grant.grantedAt) || "—"}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {grant.packTitle || "(untitled)"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {grant.productId || grant.packId || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{grant.tier || "—"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={sourceLabel(grant.source)}
                                                color={grant.source === "stripe" ? "default" : "primary"}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>
                                            {grant.transactionId || "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}
